// Message templates (Assignments popup, Production Plan).
//
// renderTemplate(body, vars): a line whose placeholders ALL resolve to empty is
// dropped (so "Product NO:" / "{Comment}" vanish on their own); lines without
// placeholders stay as they are.
//
// renderEmployeeMessage(parts): ONE message for an employee with several products
// in a day. Each template is split by its placeholders into header (lines that
// only use {Employee}/{Date} or nothing, at the top), block (product lines) and
// footer (static lines at the bottom, e.g. "Be sure to check the packaging on the
// scales"). Header once, one block per product, each distinct footer once.
// {Station}/{Brand} lines that are identical in every block move up into the
// header, so "Station: Big Station — Old" is not repeated per product.

const PH_RE = /\{(\w+)\}/g;
const EMPLOYEE_LEVEL = new Set(['Employee', 'Date']);
const HOISTABLE = new Set(['Station', 'Brand']);
const placeholders = line => (line.match(PH_RE) || []).map(p => p.slice(1, -1));

function renderLine(line, vars) {
  const found = placeholders(line);
  if (found.length && found.every(k => !vars[k])) return null;
  return line.replace(PH_RE, (_, k) => (vars[k] == null ? '' : String(vars[k])));
}

export function renderTemplate(body, vars) {
  const out = [];
  for (const line of String(body || '').split('\n')) {
    const r = renderLine(line, vars);
    if (r != null) out.push(r);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// template text → { header: [lines], block: [lines], footer: [lines] }
export function splitTemplate(body) {
  const lines = String(body || '').split('\n');
  const isProduct = l => placeholders(l).some(k => !EMPLOYEE_LEVEL.has(k));
  let first = lines.findIndex(isProduct);
  if (first < 0) return { header: lines, block: [], footer: [] };
  let last = lines.length - 1;
  while (last > first && !isProduct(lines[last])) last--;
  return { header: lines.slice(0, first), block: lines.slice(first, last + 1), footer: lines.slice(last + 1) };
}

// parts: [{ template, vars }] — one per product of the same employee, in order.
export function renderEmployeeMessage(parts) {
  if (!parts.length) return '';
  if (parts.length === 1) return renderTemplate(parts[0].template, parts[0].vars);
  const split = parts.map(p => ({ ...splitTemplate(p.template), vars: p.vars }));
  const header = split[0].header.map(l => renderLine(l, split[0].vars)).filter(l => l != null);

  // Station / Brand lines identical in every block → header, once
  const hoisted = [];
  const blocks = split.map(s => s.block.map(l => ({ line: l, text: renderLine(l, s.vars) })));
  const candidates = blocks[0].filter(x => { const p = placeholders(x.line); return p.length && p.every(k => HOISTABLE.has(k)); });
  for (const c of candidates) {
    const same = blocks.every(b => b.some(x => x.line === c.line && x.text === c.text));
    if (same && c.text != null) { hoisted.push(c.text); for (const b of blocks) { const i = b.findIndex(x => x.line === c.line); if (i >= 0) b.splice(i, 1); } }
  }

  const footers = [];
  for (const s of split) {
    const f = s.footer.map(l => renderLine(l, s.vars)).filter(l => l != null).join('\n').trim();
    if (f && !footers.includes(f)) footers.push(f);
  }
  const sections = [
    [...header, ...hoisted].join('\n'),
    ...blocks.map(b => b.map(x => x.text).filter(t => t != null).join('\n')),
    ...footers,
  ].map(s => s.trim()).filter(Boolean);
  return sections.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}
